"use client";

import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Table,
  Button,
  Input,
  Label,
  Spinner,
  Alert,
} from "reactstrap";
import { toast } from "react-toastify";
import Api from "../../api/Api";
import CampaignApplicationTableItem from "./CampaignApplicationTableItem";
import CampaignApplicationDetailsModal from "./CampaignApplicationDetailsModal";
import Breadcrumbs from "../../components/Common/Breadcrumb";

export default function CampaignApplications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalApplications, setTotalApplications] = useState(0);

  // Modal state
  const [modal_center, setmodal_center] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);

  const tog_center = () => {
    setmodal_center(!modal_center);
  };

  // Statistics state
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    completed: 0,
  });

  const fetchApplications = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");

      // Build parameters object, only including non-empty values
      const paramsObj = {
        page: page.toString(),
        per_page: "15",
      };

      // Only add parameters if they have values
      if (searchTerm && searchTerm.trim() !== "") {
        paramsObj.search = searchTerm.trim();
      }

      if (statusFilter && statusFilter !== "") {
        paramsObj.status = statusFilter;
      }

      if (sortBy && sortBy !== "") {
        paramsObj.sort_by = sortBy;
      }

      if (sortOrder && sortOrder !== "") {
        paramsObj.sort_order = sortOrder;
      }

      const params = new URLSearchParams(paramsObj);

      console.log("Fetching applications with params:", paramsObj);

      const response = await Api.get(`/admin/campaign-applications?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("API Response:", response.data);

      if (response.data.success) {
        setApplications(response.data.data || []);
        setCurrentPage(response.data.meta?.current_page || 1);
        setTotalPages(response.data.meta?.last_page || 1);
        setTotalApplications(response.data.meta?.total || 0);

        // Calculate statistics
        const allApplications = response.data.data || [];
        const newStats = {
          total: allApplications.length,
          pending: allApplications.filter((app) => app.status === "pending")
            .length,
          approved: allApplications.filter((app) => app.status === "approved")
            .length,
          rejected: allApplications.filter((app) => app.status === "rejected")
            .length,
          completed: allApplications.filter((app) => app.status === "completed")
            .length,
        };
        setStats(newStats);
      } else {
        setError(response.data.message || "Failed to fetch applications");
      }
    } catch (error) {
      console.error("Error fetching applications:", error);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications(currentPage);
  }, [searchTerm, statusFilter, sortBy, sortOrder, currentPage]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setCurrentPage(1);
  };

  const handleViewDetails = (application) => {
    setSelectedApplication(application);
    tog_center();
  };

  const handleStatusUpdate = async (applicationId, newStatus) => {
    try {
      const token = localStorage.getItem("token");
      const response = await Api.put(
        `/admin/campaign-applications/${applicationId}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success(`Application ${newStatus} successfully`, {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });

        // Update the application in the list
        setApplications((prev) =>
          prev.map((app) =>
            app.id === applicationId
              ? {
                  ...app,
                  status: newStatus,
                  reviewed_at: new Date().toISOString(),
                }
              : app
          )
        );

        // Refresh to get updated stats
        fetchApplications(currentPage);
      } else {
        toast.error(
          response.data.message || "Failed to update application status",
          {
            position: "bottom-center",
            autoClose: 3000,
            theme: "colored",
          }
        );
      }
    } catch (error) {
      console.error("Error updating application status:", error);
      toast.error("Network error. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    }
  };

  const handleDelete = async (applicationId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await Api.delete(
        `/admin/campaign-applications/${applicationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success("Application deleted successfully", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });

        // Remove the application from the list
        setApplications((prev) =>
          prev.filter((app) => app.id !== applicationId)
        );
        setTotalApplications((prev) => prev - 1);

        // Refresh to get updated stats
        fetchApplications(currentPage);
      } else {
        toast.error(response.data.message || "Failed to delete application", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      }
    } catch (error) {
      console.error("Error deleting application:", error);
      toast.error("Network error. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    }
  };

  const onStatusUpdate = (applicationId, newStatus) => {
    // Update the selected application if it's the one being viewed
    if (selectedApplication && selectedApplication.id === applicationId) {
      setSelectedApplication((prev) => ({
        ...prev,
        status: newStatus,
        reviewed_at: new Date().toISOString(),
      }));
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          color={currentPage === i ? "primary" : "light"}
          size="sm"
          onClick={() => handlePageChange(i)}
          className="me-1"
        >
          {i}
        </Button>
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          <small className="text-muted">
            Showing {applications.length} of {totalApplications} applications
          </small>
        </div>
        <div className="d-flex align-items-center">
          <Button
            color="light"
            size="sm"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="me-2"
          >
            <i className="ri-arrow-left-line"></i>
          </Button>
          {pages}
          <Button
            color="light"
            size="sm"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="ms-2"
          >
            <i className="ri-arrow-right-line"></i>
          </Button>
        </div>
      </div>
    );
  };

  if (loading && applications.length === 0) {
    return (
      <Container fluid>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <Spinner color="primary" size="lg" />
        </div>
      </Container>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            title="Campaign Applications"
            breadcrumbItem="Campaign Applications"
          />

          {/* Statistics Cards */}
          <Row className="mb-4">
            <Col xl={2} md={4} sm={6}>
              <Card className="card-height-100">
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-primary-subtle text-primary rounded-circle fs-3">
                        <i className="ri-file-user-line"></i>
                      </span>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <p className="text-uppercase fw-semibold fs-12 text-muted mb-1">
                        Total Applications
                      </p>
                      <h4 className="mb-0">{stats.total}</h4>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl={2} md={4} sm={6}>
              <Card className="card-height-100">
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-warning-subtle text-warning rounded-circle fs-3">
                        <i className="ri-time-line"></i>
                      </span>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <p className="text-uppercase fw-semibold fs-12 text-muted mb-1">
                        Pending
                      </p>
                      <h4 className="mb-0">{stats.pending}</h4>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl={2} md={4} sm={6}>
              <Card className="card-height-100">
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-success-subtle text-success rounded-circle fs-3">
                        <i className="ri-check-line"></i>
                      </span>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <p className="text-uppercase fw-semibold fs-12 text-muted mb-1">
                        Approved
                      </p>
                      <h4 className="mb-0">{stats.approved}</h4>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl={2} md={4} sm={6}>
              <Card className="card-height-100">
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-danger-subtle text-danger rounded-circle fs-3">
                        <i className="ri-close-line"></i>
                      </span>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <p className="text-uppercase fw-semibold fs-12 text-muted mb-1">
                        Rejected
                      </p>
                      <h4 className="mb-0">{stats.rejected}</h4>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl={2} md={4} sm={6}>
              <Card className="card-height-100">
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-info-subtle text-info rounded-circle fs-3">
                        <i className="ri-check-double-line"></i>
                      </span>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <p className="text-uppercase fw-semibold fs-12 text-muted mb-1">
                        Completed
                      </p>
                      <h4 className="mb-0">{stats.completed}</h4>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col>
              <Card>
                <CardHeader>
                  <Row className="align-items-center">
                    <Col>
                      <h4 className="card-title mb-0">
                        Campaign Applications Management
                      </h4>
                    </Col>
                    <Col className="col-auto">
                      <div className="d-flex gap-2">
                        <Button
                          color="light"
                          onClick={() => fetchApplications(currentPage)}
                          disabled={loading}
                        >
                          <i className="ri-refresh-line me-1"></i>
                          Refresh
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody>
                  {/* Filters */}
                  <Row className="mb-3">
                    <Col md={4}>
                      <Label for="search">Search Applications</Label>
                      <Input
                        type="text"
                        id="search"
                        placeholder="Search by influencer, campaign, or business..."
                        value={searchTerm}
                        onChange={handleSearch}
                      />
                    </Col>
                    <Col md={3}>
                      <Label for="status">Filter by Status</Label>
                      <Input
                        type="select"
                        id="status"
                        value={statusFilter}
                        onChange={(e) => handleStatusFilter(e.target.value)}
                      >
                        <option value="">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="completed">Completed</option>
                      </Input>
                    </Col>
                    <Col md={3}>
                      <Label for="sort">Sort By</Label>
                      <Input
                        type="select"
                        id="sort"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                      >
                        <option value="created_at">Application Date</option>
                        <option value="status">Status</option>
                        <option value="reviewed_at">Review Date</option>
                      </Input>
                    </Col>
                    <Col md={2}>
                      <Label for="order">Order</Label>
                      <Input
                        type="select"
                        id="order"
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value)}
                      >
                        <option value="desc">Newest First</option>
                        <option value="asc">Oldest First</option>
                      </Input>
                    </Col>
                  </Row>

                  {error && (
                    <Alert color="danger" className="mb-3">
                      <i className="ri-error-warning-line me-2"></i>
                      {error}
                    </Alert>
                  )}

                  {loading ? (
                    <div className="text-center py-4">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Loading applications...</p>
                    </div>
                  ) : applications.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="ri-file-user-line display-4 text-muted"></i>
                      <h5 className="mt-3">No Applications Found</h5>
                      <p className="text-muted">
                        {searchTerm || statusFilter
                          ? "No applications match your current filters."
                          : "No campaign applications have been submitted yet."}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <Table className="table-nowrap align-middle mb-0">
                          <thead>
                            <tr>
                              <th scope="col">#</th>
                              <th
                                scope="col"
                                className="sort cursor-pointer"
                                onClick={() => handleSort("influencer_name")}
                              >
                                Influencer
                                {sortBy === "influencer_name" && (
                                  <i
                                    className={`ri-arrow-${
                                      sortOrder === "asc" ? "up" : "down"
                                    }-line ms-1`}
                                  ></i>
                                )}
                              </th>
                              <th
                                scope="col"
                                className="sort cursor-pointer"
                                onClick={() => handleSort("campaign_title")}
                              >
                                Campaign
                                {sortBy === "campaign_title" && (
                                  <i
                                    className={`ri-arrow-${
                                      sortOrder === "asc" ? "up" : "down"
                                    }-line ms-1`}
                                  ></i>
                                )}
                              </th>
                              <th scope="col">Business</th>
                              <th
                                scope="col"
                                className="sort cursor-pointer"
                                onClick={() => handleSort("status")}
                              >
                                Status
                                {sortBy === "status" && (
                                  <i
                                    className={`ri-arrow-${
                                      sortOrder === "asc" ? "up" : "down"
                                    }-line ms-1`}
                                  ></i>
                                )}
                              </th>
                              <th
                                scope="col"
                                className="sort cursor-pointer"
                                onClick={() => handleSort("created_at")}
                              >
                                Application Date
                                {sortBy === "created_at" && (
                                  <i
                                    className={`ri-arrow-${
                                      sortOrder === "asc" ? "up" : "down"
                                    }-line ms-1`}
                                  ></i>
                                )}
                              </th>
                              <th scope="col">Influencer Stats</th>
                              <th scope="col">Message</th>
                              <th scope="col">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {applications.map((application, index) => (
                              <CampaignApplicationTableItem
                                key={application.id}
                                application={application}
                                index={(currentPage - 1) * 15 + index}
                                onViewDetails={handleViewDetails}
                                onStatusUpdate={handleStatusUpdate}
                                onDelete={handleDelete}
                              />
                            ))}
                          </tbody>
                        </Table>
                      </div>

                      {renderPagination()}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Application Details Modal */}
          <CampaignApplicationDetailsModal
            modal_center={modal_center}
            setmodal_center={setmodal_center}
            tog_center={tog_center}
            application={selectedApplication}
            onStatusUpdate={onStatusUpdate}
          />
        </Container>
      </div>
    </React.Fragment>
  );
}
